import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface PanelCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, PanelCopy> = {
  en: {
    eyebrow: "Medium-Based Separation",
    title: "Gas Liquid Separation",
    lead: "Knitted wire mesh demister pads and sintered laminates that capture droplets and mist from gas streams. This page is organized by medium, while Oil & Gas Separation (/OilGasSeparationPage) covers the upstream and downstream hydrocarbon industry.",
    points: ["Efficient mist elimination and entrained liquid droplet capture across high-velocity gas streams.", "Versatile mesh pad demisters and diffusion-bonded sintered laminates tailored to operational flow conditions.", "Comprehensive separation media by fluid phase, complementing the dedicated Oil & Gas industry page (/OilGasSeparationPage)."],
    facts: [{
      label: "Woven Mesh Range",
      value: "1 – 635 Mesh"
    }, {
      label: "Sintered Rating",
      value: "1 – 200 µm"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L / Nickel"
    }, {
      label: "Configurations",
      value: "Demister Pad / Sintered"
    }],
    imageAlt: "Knitted stainless steel wire mesh demister pad for gas liquid separation and mist elimination"
  },
  zh: {
    eyebrow: "按流体介质分类",
    title: "气液分离",
    lead: "采用不锈钢气液过滤网除沫器与烧结复合板，高效捕集工艺气体中的雾滴与微细液滴。本页面依流体介质展开，而油气分离（/OilGasSeparationPage）则面向石油天然气行业应用。",
    points: ["高效消除高速气流中夹带的微细雾滴与冷凝液滴，保障下游工艺洁净。", "提供编织丝网除沫器、针织丝网垫及多层扩散烧结复合滤材等多种构型选择。", "按介质相态提供针对性分离方案，与油气行业专项页面（/OilGasSeparationPage）形成完整互补。"],
    facts: [{
      label: "编织网规格",
      value: "1 – 635 目"
    }, {
      label: "烧结精度",
      value: "1 – 200 µm"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L / 镍基合金"
    }, {
      label: "结构形式",
      value: "除沫器丝网垫 / 烧结板"
    }],
    imageAlt: "用于气液分离与除雾除沫的不锈钢编织针织丝网除沫器"
  },
  es: {
    eyebrow: "Separación por medio",
    title: "Separación gas-líquido",
    lead: "Eliminadores de niebla de malla tejida y laminados sinterizados para capturar gotas y niebla en corrientes gaseosas. Esta página clasifica por medio, mientras que Separación de petróleo y gas (/OilGasSeparationPage) cubre la industria petrolera.",
    points: ["Eliminación eficaz de niebla y retención de gotas líquidas arrastradas en corrientes de gas de alta velocidad.", "Opciones versátiles de almohadillas desempañadoras de malla y laminados sinterizados según el caudal operativo.", "Medios de separación por fase de fluido, complementando la página especializada del sector de petróleo y gas (/OilGasSeparationPage)."],
    facts: [{
      label: "Malla tejida",
      value: "1 – 635 Mesh"
    }, {
      label: "Grado sinterizado",
      value: "1 – 200 µm"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L / Níquel"
    }, {
      label: "Configuraciones",
      value: "Desempañador / Sinterizado"
    }],
    imageAlt: "Almohadilla desempañadora de malla de alambre de acero inoxidable para separación gas-líquido"
  },
  de: {
    eyebrow: "Medienbasierte Trennung",
    title: "Gas-Flüssig-Trennung",
    lead: "Gestrickte Tropfenabscheider-Drahtgestricke und Sinterlaminate zur Abscheidung von Tröpfchen und Nebel aus Gasströmen. Diese Seite gliedert nach Medium, während die Öl- und Gastrennung (/OilGasSeparationPage) die Öl- und Gasindustrie abdeckt.",
    points: ["Effiziente Tropfenabscheidung und Abscheidung mitgerissener Flüssigkeitstropfen in schnellen Gasströmen.", "Vielseitige Gestrick-Tropfenabscheider und diffusionsgebundene Sinterlaminate für spezifische Betriebsbedingungen.", "Medienbasierte Trennung nach Fluidphase in Ergänzung zur branchenspezifischen Öl- und Gasseite (/OilGasSeparationPage)."],
    facts: [{
      label: "Drahtgewebe",
      value: "1 – 635 Mesh"
    }, {
      label: "Sinterfeinheit",
      value: "1 – 200 µm"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L / Nickel"
    }, {
      label: "Ausführungen",
      value: "Tropfenabscheider / Sinterelement"
    }],
    imageAlt: "Edelstahldrahtgestrick-Tropfenabscheider für die Gas-Flüssig-Trennung und Nebelabscheidung"
  },
  fr: {
    eyebrow: "Séparation par fluide",
    title: "Séparation gaz-liquide",
    lead: "Matelas dévésiculeurs en tricot métallique et laminés frittés capturant gouttelettes et brouillards dans les flux gazeux. Cette page classe par fluide, tandis que la page Séparation pétrole et gaz (/OilGasSeparationPage) cible l'industrie pétrolière.",
    points: ["Élimination efficace des brouillards et capture des gouttelettes liquides entraînées sous vitesse élevée.", "Options de matelas dévésiculeurs en fil tricoté et laminés frittés adaptées aux débits opérationnels.", "Médias de séparation par phase fluide complétant la page dédiée à l'industrie pétrolière et gazière (/OilGasSeparationPage)."],
    facts: [{
      label: "Gamme tissée",
      value: "1 – 635 mesh"
    }, {
      label: "Seuil fritté",
      value: "1 – 200 µm"
    }, {
      label: "Alliages",
      value: "AISI 304 / 316L / Nickel"
    }, {
      label: "Formats",
      value: "Dévésiculeur / Fritté"
    }],
    imageAlt: "Matelas dévésiculeur en tricot de fil inox pour la séparation gaz-liquide et l'élimination des brouillards"
  },
  pt: {
    eyebrow: "Separação por meio",
    title: "Separação gás-líquido",
    lead: "Almofadas eliminadoras de névoa em malha tricotada e laminados sinterizados para capturar gotículas e névoa em correntes de gás. Esta página aborda o meio, enquanto Separação de petróleo e gás (/OilGasSeparationPage) atende à indústria de óleo e gás.",
    points: ["Eliminação eficiente de névoa e captura de gotículas de líquido arrastadas em correntes de gás de alta velocidade.", "Opções versáteis de almofadas eliminadoras de névoa e laminados sinterizados adequados às vazões operacionais.", "Meios de separação por fase fluida em complemento à página dedicada da indústria de óleo e gás (/OilGasSeparationPage)."],
    facts: [{
      label: "Faixa de malha",
      value: "1 – 635 Mesh"
    }, {
      label: "Grau sinterizado",
      value: "1 – 200 µm"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L / Níquel"
    }, {
      label: "Formatos",
      value: "Eliminador de névoa / Sinterizado"
    }],
    imageAlt: "Almofada eliminadora de névoa em malha de arame tricotada de aço inoxidável para separação gás-líquido"
  }
};
export default function GasLiquidSummary() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="gas-liquid-separation" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} href="/GasLiquidSeparationPage" image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-knitted-sta-a66f45.png",
    alt: copy.imageAlt
  }} tone="muted" />;
}